/*
 * Color palette component
 *
 * it does:
 * * renders a list of color buttons
 *
 * */
import { observer } from "mobx-react";
import React, { ReactElement } from "react";
import { Transition } from "react-transition-group";
import classNames from "classnames";

import { Tick } from "../../icons";
import { DEFAULT_GREY10, IColor } from "../../../utils/color/defaultColors";
import { areColorsEquivalent, shouldUseDarkText } from "../../../utils/color/colorUtil";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";

interface IColorPaletteProps {
  presetColors: IColor[];
  handleColorChange: (color: IColor) => void;
  selectedThemeIndex?: number | null;
}

const defaultProps: Partial<IColorPaletteProps> = {
  selectedThemeIndex: null,
};

const ColorPalette: React.FC<IColorPaletteProps> = observer(
  ({ presetColors, selectedThemeIndex, handleColorChange }) => {
    const handleSelectedColorChange = (color: IColor) => {
      handleColorChange(color);
    };

    const getSelectedColorValue = () => {
      const result = [...presetColors].find((color) => color.themeIndex === selectedThemeIndex);
      return !!result ? result.value : "";
    };

    const selectedColor = getSelectedColorValue();

    return (
      <div className="color-palette">
        <div className="color-palette__color-container">
          {presetColors.map((color, index) => (
            <div
              key={`${color.value}-${index}`}
              onClick={() => handleSelectedColorChange(color)}
              className="color-palette__color-button"
              style={{
                backgroundColor: color.value,
                color: shouldUseDarkText(color.value) ? "#4A4A4A" : "white",
              }}
            >
              <Transition
                mountOnEnter
                unmountOnExit
                in={areColorsEquivalent(selectedColor, color.value)}
                timeout={AnimationDuration.Medium}
                onEnter={forceReflow}
              >
                {(status): ReactElement => (
                  <div
                    className={classNames("color-palette__icon", `color-palette__icon--${status}`, {
                      "color-palette__icon--dark": selectedThemeIndex === DEFAULT_GREY10.themeIndex,
                    })}
                  >
                    <Tick />
                  </div>
                )}
              </Transition>
            </div>
          ))}
        </div>
      </div>
    );
  },
);

ColorPalette.defaultProps = defaultProps;

export default ColorPalette;
